import { z } from "zod";

export const experienceLevelSchema = z.enum(["none", "lt1", "1to3", "gt3"]);
export type ExperienceLevel = z.infer<typeof experienceLevelSchema>;

const monthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Expected YYYY-MM");

export const roleInterestRequestSchema = z
  .object({
    roleKey: z.string().min(1),
    level: experienceLevelSchema,
    restaurant: z.string().trim().optional(),
    startMonth: monthSchema.optional(),
    endMonth: monthSchema.optional(),
    current: z.boolean().default(false),
  })
  .superRefine((val, ctx) => {
    if (val.level === "none") return;
    if (!val.restaurant || val.restaurant.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["restaurant"],
        message: "Restaurant name is required.",
      });
    }
    if (!val.startMonth) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startMonth"],
        message: "Start date is required.",
      });
    }
    if (!val.current && !val.endMonth) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endMonth"],
        message: "Provide an end date or mark as current.",
      });
    }
  });
export type RoleInterestRequest = z.infer<typeof roleInterestRequestSchema>;

// New schema for the role_experience table — mirrors roleInterestRequestSchema
// but is sent under the `roleExperiences` key.
export const roleExperienceRequestSchema = roleInterestRequestSchema;
export type RoleExperienceRequest = z.infer<typeof roleExperienceRequestSchema>;

export const locationRequestSchema = z.object({
  city: z.string().min(1),
  radiusMiles: z.number().int().min(2).max(50),
  openToRelocate: z.boolean().default(false),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});
export type LocationRequest = z.infer<typeof locationRequestSchema>;

export const onboardingProfileRequestSchema = z.object({
  fullName: z.string().max(120).optional(),
  location: locationRequestSchema.optional(),
  roleInterests: z.array(roleInterestRequestSchema).default([]),
  roleExperiences: z.array(roleExperienceRequestSchema).optional(),
});
export type OnboardingProfileRequest = z.infer<
  typeof onboardingProfileRequestSchema
>;

export const roleInterestResponseSchema = z.object({
  roleKey: z.string(),
  level: experienceLevelSchema,
  restaurant: z.string().nullish(),
  startMonth: z.string().nullish(),
  endMonth: z.string().nullish(),
  current: z.boolean(),
});

export const locationResponseSchema = z
  .object({
    city: z.string().nullish(),
    radiusMiles: z.number().int().nullish(),
    openToRelocate: z.boolean(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
  })
  .nullable();

export const onboardingProfileResponseSchema = z.object({
  userId: z.string(),
  email: z.string().nullish(),
  fullName: z.string().nullish(),
  status: z.enum(["DRAFT", "COMPLETE"]),
  location: locationResponseSchema,
  roleInterests: z.array(roleInterestResponseSchema),
  roleExperiences: z.array(roleInterestResponseSchema).optional(),
  resumeUrl: z.string().nullish(),
  photoUrl: z.string().nullish(),
  updatedAt: z.string(),
});
export type OnboardingProfileResponse = z.infer<
  typeof onboardingProfileResponseSchema
>;

export const onboardingCompletionResponseSchema = z.object({
  profileComplete: z.boolean(),
  matchFeedPath: z.string(),
});
export type OnboardingCompletionResponse = z.infer<
  typeof onboardingCompletionResponseSchema
>;

export const onboardingFieldErrorSchema = z.object({
  field: z.string(),
  message: z.string(),
});
export const onboardingValidationErrorSchema = z.object({
  success: z.boolean().optional(),
  message: z.string(),
  fieldErrors: z.array(onboardingFieldErrorSchema).default([]),
});
export type OnboardingValidationError = z.infer<
  typeof onboardingValidationErrorSchema
>;
